import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { fetchInstitution } from "../lib/api";
import axios from "axios";
import { Seo, slugify } from "../components/Seo";
import { SiteHeader } from "../components/SiteHeader";
import { SiteFooter } from "../components/SiteFooter";
import { RelatedInstitutions } from "../components/RelatedInstitutions";
import NotFound from "./NotFound";
import {
  PrivateCourtBanner,
  PrivateCourtSidebar,
  PrivateCourtFooterStrip,
} from "../components/PrivateCourtAd";
import {
  Mail,
  Phone,
  MapPin,
  Globe,
  Building2,
  ChevronRight,
  ShieldCheck,
  AlertCircle,
  Eye,
} from "lucide-react";

const API = "/api";

const maskEmail = (e) => {
  if (!e) return "";
  const [u, d] = e.split("@");
  if (!d) return e.slice(0, 2) + "•••";
  const uMask = u.length <= 2 ? u[0] + "•••" : u.slice(0, 2) + "•••" + u.slice(-1);
  return `${uMask}@${d}`;
};
const maskPhone = (p) => {
  if (!p) return "";
  const digits = p.replace(/\D/g, "");
  if (digits.length < 6) return "•••";
  return p.replace(digits.slice(2, -3), "•".repeat(Math.max(3, digits.length - 5)));
};

const roleOrder = [
  "Principal Nodal Officer",
  "Grievance Redressal Officer",
  "Recovery Head",
  "Legal Head",
  "Customer Care",
];

const ContactBlock = ({ c }) => {
  const [revealed, setRevealed] = useState(false);
  const isRec = c.role?.toLowerCase().includes("recovery");
  const isLeg = c.role?.toLowerCase().includes("legal");
  const accent = isRec ? "#FF3333" : isLeg ? "#002FA7" : "#0A0A0A";
  return (
    <article
      className="border border-[#E5E7EB] p-5 bg-white hover:border-[#0A0A0A] transition-colors"
      style={{ userSelect: revealed ? "text" : "none" }}
      onContextMenu={(e) => !revealed && e.preventDefault()}
      itemScope
      itemType="https://schema.org/Person"
    >
      <div className="mb-3">
        <span
          className="font-mono text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1 border"
          style={{ color: accent, borderColor: accent }}
        >
          {c.role}
        </span>
      </div>
      {c.name && (
        <div className="font-display font-bold text-lg leading-tight" itemProp="name">
          {c.name}
        </div>
      )}
      {c.designation && (
        <div className="text-sm text-[#4B5563] mt-0.5" itemProp="jobTitle">
          {c.designation}
        </div>
      )}
      <div className="mt-4 space-y-2 text-sm">
        {c.email && (
          revealed ? (
            <a href={`mailto:${c.email}`} className="flex items-start gap-2 hover:text-[#002FA7] break-all">
              <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span className="font-mono text-xs" itemProp="email">{c.email}</span>
            </a>
          ) : (
            <div className="flex items-start gap-2 text-[#4B5563]">
              <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span className="font-mono text-xs">{maskEmail(c.email)}</span>
            </div>
          )
        )}
        {c.phone && (
          revealed ? (
            <a href={`tel:${c.phone}`} className="flex items-start gap-2 hover:text-[#002FA7]">
              <Phone className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span className="font-mono text-xs" itemProp="telephone">{c.phone}</span>
            </a>
          ) : (
            <div className="flex items-start gap-2 text-[#4B5563]">
              <Phone className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span className="font-mono text-xs">{maskPhone(c.phone)}</span>
            </div>
          )
        )}
        {c.address && (
          <div className="flex items-start gap-2 text-[#4B5563]">
            <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span className="text-xs leading-relaxed">{c.address}</span>
          </div>
        )}
        {!revealed && (c.email || c.phone) && (
          <button
            type="button"
            onClick={() => setRevealed(true)}
            className="mt-2 inline-flex items-center gap-2 border border-[#0A0A0A] px-3 py-1.5 text-xs font-mono uppercase tracking-[0.15em] hover:bg-[#0A0A0A] hover:text-white transition-colors"
          >
            <Eye className="w-3.5 h-3.5" /> Reveal contact
          </button>
        )}
      </div>
    </article>
  );
};

export default function InstitutionPage() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/institutions/by-slug/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen">
        <SiteHeader />
        <div className="max-w-4xl mx-auto px-6 py-24 font-mono text-xs uppercase tracking-[0.2em] text-[#4B5563]">
          Loading institution…
        </div>
      </div>
    );
  }
  if (notFound || !data) {
    // Reuse the smart 404 page — includes fuzzy suggestions against 9,381 institutions.
    return <NotFound sourcePath={location.pathname} />;
  }

  const sorted = (data.contacts || []).slice().sort(
    (a, b) => {
      const ai = roleOrder.indexOf(a.role);
      const bi = roleOrder.indexOf(b.role);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    }
  );

  const typeSlug = data.type === "Bank" ? "bank" : "nbfc";
  const categorySlug = slugify(data.category);
  const stateSlug = slugify(data.hq_state);

  const rolesPresent = sorted.map((c) => c.role).join(", ");
  const description =
    `${data.name} — ${data.category} in ${data.hq_city}, ${data.hq_state}. ` +
    `Contact details of ${rolesPresent || "nodal officer"} including email, phone and address. ` +
    `Public directory compiled from RBI disclosures.`;

  const seoKeywords = [
    `${data.name} nodal officer`,
    `${data.name} grievance officer`,
    `${data.name} recovery head`,
    `${data.name} legal head`,
    `${data.name} contact email`,
    `${data.name} customer care`,
    `${data.name} complaint email id`,
    `${data.name} ${data.hq_city}`,
    `${data.name} head office address`,
    `${data.category} ${data.hq_state}`,
    ...(data.rbi_license_no ? [`${data.name} RBI CoR ${data.rbi_license_no}`] : []),
    ...sorted.filter((c) => c.name).map((c) => `${c.name} ${data.name}`),
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FinancialService",
    name: data.name,
    description,
    url: data.website || `https://bankdesks.com/${typeSlug}/${data.slug}`,
    address: {
      "@type": "PostalAddress",
      addressLocality: data.hq_city,
      addressRegion: data.hq_state,
      addressCountry: "IN",
    },
    identifier: data.rbi_license_no,
    areaServed: "IN",
    contactPoint: sorted.map((c) => ({
      "@type": "ContactPoint",
      contactType: c.role,
      name: c.name,
      email: c.email,
      telephone: c.phone,
      areaServed: "IN",
    })),
  };

  return (
    <div className="min-h-screen bg-white text-[#0A0A0A]">
      <Seo
        title={`${data.name} — Nodal, Grievance, Recovery & Legal Head Contacts`}
        description={description}
        keywords={seoKeywords}
        path={`/${typeSlug}/${data.slug}`}
        type="article"
        publishedTime={data.created_at || undefined}
        modifiedTime={data.updated_at || data.last_enriched_at || undefined}
        jsonLd={jsonLd}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: data.type === "Bank" ? "Banks" : "NBFCs", path: `/?type=${data.type}` },
          { name: data.category, path: `/category/${categorySlug}` },
          { name: data.hq_state, path: `/state/${stateSlug}` },
          { name: data.name, path: `/${typeSlug}/${data.slug}` },
        ]}
      />
      <SiteHeader />

      {/* Breadcrumbs */}
      <nav
        className="border-b border-[#E5E7EB] bg-[#F8F9FA]"
        aria-label="Breadcrumb"
      >
        <ol className="max-w-[1440px] mx-auto px-6 md:px-8 lg:px-12 py-3 flex flex-wrap items-center gap-2 text-xs font-mono text-[#4B5563]">
          <li><Link to="/" className="hover:text-[#002FA7]">Home</Link></li>
          <li><ChevronRight className="w-3 h-3" /></li>
          <li><Link to={`/category/${categorySlug}`} className="hover:text-[#002FA7]">{data.category}</Link></li>
          <li><ChevronRight className="w-3 h-3" /></li>
          <li><Link to={`/state/${stateSlug}`} className="hover:text-[#002FA7]">{data.hq_state}</Link></li>
          <li><ChevronRight className="w-3 h-3" /></li>
          <li className="text-[#0A0A0A] font-bold truncate">{data.name}</li>
        </ol>
      </nav>

      {/* Hero */}
      <section className="border-b border-[#0A0A0A]" itemScope itemType="https://schema.org/FinancialService">
        <div className="max-w-[1440px] mx-auto px-6 md:px-8 lg:px-12 py-12 md:py-16">
          <div className="flex items-start gap-5 flex-wrap">
            <div className="w-14 h-14 bg-[#002FA7] flex items-center justify-center flex-shrink-0">
              <Building2 className="w-7 h-7 text-white" strokeWidth={2.25} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563]">
                {data.type} · <span itemProp="category">{data.category}</span>
              </div>
              <h1
                className="font-display font-black text-3xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.05] mt-2"
                itemProp="name"
              >
                {data.name}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                {data.has_detailed_contacts ? (
                  <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-1 text-[10px] uppercase tracking-wider font-bold">
                    <ShieldCheck className="w-3 h-3" /> Verified contacts
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-800 border border-amber-200 px-2 py-1 text-[10px] uppercase tracking-wider font-bold">
                    <AlertCircle className="w-3 h-3" /> From RBI master list
                  </span>
                )}
                <span className="text-sm text-[#4B5563]" itemProp="areaServed">
                  HQ: {data.hq_city}, {data.hq_state}
                </span>
                {data.website && (
                  <a
                    href={data.website}
                    target="_blank"
                    rel="noreferrer"
                    itemProp="url"
                    className="inline-flex items-center gap-1 text-sm text-[#002FA7] hover:underline"
                  >
                    <Globe className="w-3.5 h-3.5" />
                    {data.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main */}
      <main className="max-w-[1440px] mx-auto px-6 md:px-8 lg:px-12 py-12 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-10">
        <div>
          <h2 className="font-display font-bold text-2xl tracking-tight mb-5">
            Escalation Contacts <span className="text-[#4B5563] font-normal">({sorted.length})</span>
          </h2>

          {!data.has_detailed_contacts && (
            <div className="mb-5 p-4 border-l-4 border-[#FF3333] bg-red-50 font-mono text-[11px] leading-relaxed text-red-900">
              <div className="font-bold uppercase tracking-[0.15em] mb-1">Unverified entry</div>
              We only have the public RBI master-list info for <strong>{data.name}</strong>.
              Recovery Head / Legal Head not yet curated — verify on the official website before any legal action.
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sorted.length === 0 ? (
              <div className="col-span-2 p-8 border border-dashed border-[#D1D5DB] text-center text-sm text-[#4B5563]">
                No contacts on file. Visit the official website above.
              </div>
            ) : sorted.map((c, i) => <ContactBlock key={i} c={c} />)}
          </div>

          <section className="mt-12">
            <h2 className="font-display font-bold text-xl tracking-tight mb-3">
              About {data.name}
            </h2>
            <p className="text-[#4B5563] leading-relaxed">
              {data.name} is {data.category === "Public Sector Bank" ? "a" : "an"} <strong>{data.category}</strong>
              {" "}headquartered in {data.hq_city}, {data.hq_state}, India.
              {data.rbi_license_no && <> Registered with the Reserve Bank of India under CoR <strong>{data.rbi_license_no}</strong>.</>}
              {" "}Customers and counsel can use the contacts above for grievance, recovery and legal escalations.
            </p>

            <h3 className="font-display font-bold text-lg mt-8 mb-3">
              Frequently asked
            </h3>
            <dl className="space-y-4 text-sm text-[#4B5563]">
              <div>
                <dt className="font-bold text-[#0A0A0A]">Who is the Principal Nodal Officer of {data.name}?</dt>
                <dd className="mt-1">
                  {sorted.find((c) => c.role === "Principal Nodal Officer")?.name ||
                    `Not published — verify via ${data.website || "the official website"}.`}
                </dd>
              </div>
              <div>
                <dt className="font-bold text-[#0A0A0A]">How do I escalate a complaint against {data.name}?</dt>
                <dd className="mt-1">
                  Start with the Grievance Redressal Officer listed above. If unresolved in 30 days, escalate to
                  the RBI Ombudsman at <a href="https://cms.rbi.org.in" className="text-[#002FA7] underline">cms.rbi.org.in</a>.
                </dd>
              </div>
              <div>
                <dt className="font-bold text-[#0A0A0A]">Is {data.name} registered with RBI?</dt>
                <dd className="mt-1">
                  Yes. {data.name} appears in the official RBI master list of {data.type === "Bank" ? "scheduled banks" : "registered NBFCs / ARCs"}
                  {data.rbi_license_no && <> under CoR {data.rbi_license_no}</>}.
                </dd>
              </div>
            </dl>
          </section>

          <div className="mt-10">
            <PrivateCourtBanner />
          </div>

          <RelatedInstitutions
            category={data.category}
            state={data.hq_state}
            type={data.type}
            excludeSlug={data.slug}
          />
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          <div className="border-2 border-[#FF3333] p-5 bg-white">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF3333] mb-2">
              Need help resolving?
            </div>
            <h3 className="font-display font-black text-lg tracking-tight leading-tight mb-2">
              Had a bad experience with {data.short_name || data.name}?
            </h3>
            <p className="text-xs text-[#4B5563] mb-4 leading-relaxed">
              Submit your issue — our operators will ping the nodal officer
              using the contacts above and follow up on your behalf.
            </p>
            <Link
              to={`/resolve?institution=${encodeURIComponent(data.name)}&slug=${encodeURIComponent(data.slug || "")}`}
              data-testid="institution-resolve-cta"
              className="block text-center bg-[#0A0A0A] text-white font-bold px-4 py-2.5 text-xs uppercase tracking-[0.15em] hover:bg-[#FF3333] transition-colors"
            >
              Report an issue →
            </Link>
          </div>

          <div className="border border-[#0A0A0A] p-5 bg-[#F8F9FA]">
            <h3 className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563] mb-3">
              Quick facts
            </h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between gap-4"><dt className="text-[#4B5563]">Type</dt><dd className="font-bold">{data.type}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-[#4B5563]">Category</dt><dd className="font-bold text-right">{data.category}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-[#4B5563]">HQ City</dt><dd className="font-bold text-right">{data.hq_city}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-[#4B5563]">HQ State</dt><dd className="font-bold text-right">{data.hq_state}</dd></div>
              {data.rbi_license_no && (
                <div className="flex justify-between gap-4"><dt className="text-[#4B5563]">RBI CoR</dt><dd className="font-mono text-xs text-right">{data.rbi_license_no}</dd></div>
              )}
            </dl>
          </div>
          <PrivateCourtSidebar />
        </aside>
      </main>

      <PrivateCourtFooterStrip />
      <SiteFooter />
    </div>
  );
}
